import { LitElement, html } from 'lit';
import styles from './studio-stepper.css.mjs';
import '../studio-icon/studio-icon.mjs';

export class StudioStepper extends LitElement {
  static styles = styles;

  static properties = {
    styleType: { type: String, reflect: true, attribute: 'style-type' },
    disabled: { type: Boolean, reflect: true },
  };

  static validStyleTypes = ['outline', 'filled'];

  constructor() {
    super();
    this.styleType = 'outline';
    this.disabled = false;
    this._eventListeners = [];
  }

  set styleType(value) {
    this._styleType = StudioStepper.validStyleTypes.includes(value) ? value : 'outline';
  }

  get styleType() {
    return this._styleType;
  }

  firstUpdated() {
    const minusButton = this.renderRoot.querySelector('.minus');
    const plusButton = this.renderRoot.querySelector('.plus');
    if (!minusButton || !plusButton) return;

    const setState = (button, state, add = true) => {
      if (!this.disabled) {
        button.classList[add ? 'add' : 'remove'](state);
      }
    };

    this._eventListeners = [
      {
        element: minusButton,
        event: 'click',
        handler: () => this._handleMinusClick(),
      },
      {
        element: plusButton,
        event: 'click',
        handler: () => this._handlePlusClick(),
      },
      {
        element: minusButton,
        event: 'mouseenter',
        handler: () => setState(minusButton, 'hover'),
      },
      {
        element: minusButton,
        event: 'mouseleave',
        handler: () => setState(minusButton, 'hover', false),
      },
      {
        element: minusButton,
        event: 'focusin',
        handler: () => setState(minusButton, 'focus'),
      },
      {
        element: minusButton,
        event: 'focusout',
        handler: () => setState(minusButton, 'focus', false),
      },
      {
        element: minusButton,
        event: 'mousedown',
        handler: () => setState(minusButton, 'press'),
      },
      {
        element: minusButton,
        event: 'mouseup',
        handler: () => setState(minusButton, 'focus'),
      },
      {
        element: minusButton,
        event: 'keydown',
        handler: (e) => {
          if (!this.disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            this._handleMinusClick();
          }
        },
      },
      {
        element: plusButton,
        event: 'mouseenter',
        handler: () => setState(plusButton, 'hover'),
      },
      {
        element: plusButton,
        event: 'mouseleave',
        handler: () => setState(plusButton, 'hover', false),
      },
      {
        element: plusButton,
        event: 'focusin',
        handler: () => setState(plusButton, 'focus'),
      },
      {
        element: plusButton,
        event: 'focusout',
        handler: () => setState(plusButton, 'focus', false),
      },
      {
        element: plusButton,
        event: 'mousedown',
        handler: () => setState(plusButton, 'press'),
      },
      {
        element: plusButton,
        event: 'mouseup',
        handler: () => setState(plusButton, 'focus'),
      },
      {
        element: plusButton,
        event: 'keydown',
        handler: (e) => {
          if (!this.disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            this._handlePlusClick();
          }
        },
      },
    ];

    this._eventListeners.forEach(({ element, event, handler }) =>
      element.addEventListener(event, handler)
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._eventListeners.forEach(({ element, event, handler }) =>
      element.removeEventListener(event, handler)
    );
    this._eventListeners = [];
  }

  render() {
    return html`
      <div
        class="stepper"
        role="spinbutton"
        aria-disabled="${this.disabled}"
        aria-label="Stepper control"
      >
        <button
          class="minus"
          ?disabled="${this.disabled}"
          aria-label="Decrement"
        >
          <slot name="minus-icon">
            <studio-icon name="minus" size="18px" color="currentColor"></studio-icon>
          </slot>
        </button>
        <span class="divider"></span>
        <button
          class="plus"
          ?disabled="${this.disabled}"
          aria-label="Increment"
        >
          <slot name="plus-icon">
            <studio-icon name="plus" size="18px" color="currentColor"></studio-icon>
          </slot>
        </button>
        <slot></slot>
      </div>
    `;
  }

  _handleMinusClick() {
    if (!this.disabled) {
      this.dispatchEvent(new CustomEvent('decrement', { bubbles: true, composed: true }));
    }
  }

  _handlePlusClick() {
    if (!this.disabled) {
      this.dispatchEvent(new CustomEvent('increment', { bubbles: true, composed: true }));
    }
  }
}

customElements.define('studio-stepper', StudioStepper);